import React, { useEffect, useState } from 'react';
import { Loader2, Maximize2, Upload } from 'lucide-react';
import { useTheme } from '../../lib/theme';
import { subscribe, snapshotState, clearQueue } from '../../lib/uploads';

/**
 * Floating dock shown when the Upload modal is minimized while a queue is
 * still active (or finished but un-acknowledged). Click to re-open the modal.
 */
export default function UploadsDock({ visible, onOpen }) {
  const { colors } = useTheme();
  const [state, setState] = useState(() => snapshotState());

  useEffect(() => subscribe(setState), []);

  if (!visible || state.total === 0) return null;

  const settled = state.done + state.failed + state.skipped;
  const label = state.running
    ? `Uploading ${settled + 1 > state.total ? state.total : settled + 1} of ${state.total}`
    : state.allSettled
      ? `${state.done} uploaded${state.failed ? ` · ${state.failed} failed` : ''}`
      : `${state.queued} queued`;

  return (
    <div
      data-testid="uploads-dock"
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen?.(); }}
      className="fixed bottom-16 right-4 z-40 shadow-lg rounded-xl flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors hover:opacity-90"
      style={{
        backgroundColor: colors.surface,
        border: `1px solid ${colors.border}`,
        minWidth: 240,
      }}
    >
      {state.running ? (
        <Loader2 size={16} className="animate-spin" style={{ color: colors.accent }} />
      ) : (
        <Upload size={16} style={{ color: colors.accent }} />
      )}
      <div className="min-w-0 flex-1">
        <div className="text-sm font-semibold truncate" style={{ color: colors.text }}>
          {label}
        </div>
        {state.processing ? (
          <div className="text-xs truncate" style={{ color: colors.textMuted }} title={state.processing.name}>
            {state.processing.name}
          </div>
        ) : null}
      </div>
      {state.allSettled ? (
        <button
          data-testid="uploads-dock-dismiss"
          onClick={(e) => { e.stopPropagation(); clearQueue(); }}
          className="text-xs font-semibold hover:underline"
          style={{ color: colors.textMuted }}
        >
          Dismiss
        </button>
      ) : (
        <Maximize2 size={14} style={{ color: colors.textMuted }} />
      )}
    </div>
  );
}
